import type { ReactNode } from 'react'
import './viz.css'

interface DonutSlice {
  label: string
  value: number
  color: string
}

interface OuterSlice {
  value: number
  color: string
}

interface DonutProps<S extends DonutSlice> {
  slices: S[]
  outer?: OuterSlice[]
  centerLabel: string
  centerSub?: string
  legend?: boolean
  legendExtra?: (slice: S) => ReactNode
  ariaLabel: string
}

const CX = 50
const CY = 50
const INNER_R = 33
const INNER_W = 13
const OUTER_R = 45
const OUTER_W = 3
const CENTER_FONT = 12
const SUB_FONT = 9
const GAP = 0.03
const START = -Math.PI / 2

const positive = (v: number) => (Number.isFinite(v) && v > 0 ? v : 0)
const fmt = (n: number) => n.toFixed(2)

/** SVG elliptical arc path from angle a0 to a1 (radians, clockwise). */
function arc(cx: number, cy: number, r: number, a0: number, a1: number): string {
  const x0 = cx + r * Math.cos(a0)
  const y0 = cy + r * Math.sin(a0)
  const x1 = cx + r * Math.cos(a1)
  const y1 = cy + r * Math.sin(a1)
  const large = a1 - a0 > Math.PI ? 1 : 0
  return `M ${fmt(x0)} ${fmt(y0)} A ${r} ${r} 0 ${large} 1 ${fmt(x1)} ${fmt(y1)}`
}

/** Angle ranges per value, sized by the values' own sum. Zero-sum -> none. */
function arcs(values: number[], r: number): Array<string | null> {
  const total = values.reduce((sum, v) => sum + positive(v), 0)
  if (total <= 0) return values.map(() => null)
  let cursor = START
  return values.map((v) => {
    const span = (positive(v) / total) * 2 * Math.PI
    const a0 = cursor
    cursor += span
    if (span <= 0) return null
    const gap = span > GAP * 2 ? GAP : 0
    return arc(CX, CY, r, a0 + gap / 2, cursor - gap / 2)
  })
}

const formatPct = (value: number) =>
  Number.isFinite(value) ? `${Number(value.toFixed(1))}%` : '—'

/** Ring chart with an optional thin outer ring and a centre label. */
export function Donut<S extends DonutSlice>({
  slices,
  outer,
  centerLabel,
  centerSub,
  legend = false,
  legendExtra,
  ariaLabel,
}: DonutProps<S>) {
  const innerPaths = arcs(
    slices.map((s) => s.value),
    INNER_R,
  )
  const outerPaths = arcs(
    (outer ?? []).map((s) => s.value),
    OUTER_R,
  )

  return (
    <div className="viz-donut">
      <svg className="viz-donut-svg" viewBox="0 0 100 100" role="img" aria-label={ariaLabel}>
        <circle
          className="viz-donut-track"
          cx={CX}
          cy={CY}
          r={INNER_R}
          fill="none"
          strokeWidth={INNER_W}
        />
        {slices.map((slice, i) =>
          innerPaths[i] ? (
            <path
              key={`${slice.label}-${i}`}
              className="viz-donut-inner"
              d={innerPaths[i] as string}
              fill="none"
              stroke={slice.color}
              strokeWidth={INNER_W}
            />
          ) : null,
        )}
        {(outer ?? []).map((slice, i) =>
          outerPaths[i] ? (
            <path
              key={i}
              className="viz-donut-outer"
              d={outerPaths[i] as string}
              fill="none"
              stroke={slice.color}
              strokeWidth={OUTER_W}
            />
          ) : null,
        )}
        {/* Sizes are viewBox units (the ring renders at 96px, so 12 ≈ 11.5px);
            viz.css deliberately sets no font-size on these, which would
            override the presentation attribute. */}
        <text
          className="viz-donut-center"
          x={CX}
          y={centerSub ? 46 : 50}
          fontSize={CENTER_FONT}
          textAnchor="middle"
          dominantBaseline="central"
        >
          {centerLabel}
        </text>
        {centerSub && (
          <text
            className="viz-donut-sub"
            x={CX}
            y={59}
            fontSize={SUB_FONT}
            textAnchor="middle"
            dominantBaseline="central"
          >
            {centerSub}
          </text>
        )}
      </svg>
      {legend && (
        <div className="viz-donut-legend">
          {slices.map((slice, i) => (
            // Labels ellipsise to one line (viz.css); the full name is the title.
            <div className="viz-donut-legend-row" key={`${slice.label}-${i}`} title={slice.label}>
              <i style={{ background: slice.color }} />
              <span className="viz-donut-legend-label">{slice.label}</span>
              <span className="viz-donut-legend-pct">{formatPct(slice.value)}</span>
              {legendExtra?.(slice)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
